import { ErrorBoundary } from "@/components/error-boundary";
import { useSearchPageInteractionStore } from "@/providers/search";
import { useOptimizedClicked } from "@/providers/search/hooks/use-optimized-clicked";
import { usePropertyInteractionStore } from "@/stores/property-interaction";
import type { MapItemSUP, MapItemWithCoodId } from "@/types/search";
import { logger } from "@/utils/logger";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { Eye } from "lucide-react";
import React from "react";

import { SafePin } from "./safe-pin";

const MarkerFallback = ({ children }: { children?: React.ReactNode }) => (
	<div className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs relative">
		<div className="absolute top-full left-0 z-[-1]">{children}</div>
	</div>
);

// Helper for deep comparison of sups array
function areSupsEqual(sups1?: MapItemSUP[], sups2?: MapItemSUP[]) {
	if (sups1 === sups2) return true;
	if (!sups1 || !sups2) return false;
	if (sups1.length !== sups2.length) return false;

	// Compare IDs to ensure content hasn't changed
	for (let i = 0; i < sups1.length; i++) {
		if (sups1[i]._id !== sups2[i]._id) return false;
	}
	return true;
}

// Memoized individual marker component to prevent unnecessary re-renders
export const Marker = React.memo(
	function MarkerComponent({ marker }: { marker: MapItemWithCoodId }) {
		// Parse coordinates once
		const coordinates = React.useMemo(() => {
			if (!marker || !marker._id) return null;

			let coords: [number, number];
			if (typeof marker._id === "string") {
				try {
					coords = JSON.parse(`[${marker._id}]`);
				} catch (err) {
					logger.error("Failed to parse marker coordinates:", err);
					return null;
				}
			} else if (Array.isArray(marker._id)) {
				coords = marker._id as [number, number];
			} else {
				logger.error("Invalid marker _id format:", marker._id);
				return null;
			}

			if (!Array.isArray(coords) || coords.length !== 2 || isNaN(coords[0]) || isNaN(coords[1])) {
				logger.error("Invalid coordinates:" + JSON.stringify(coords), coords);
				return null;
			}

			return coords;
		}, [marker]);

		const coordinatesString = React.useMemo(() => coordinates?.toString() || "", [coordinates]);

		// Zero-rerender hover system using refs
		const markerRef = React.useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
		const safePinRef = React.useRef<HTMLDivElement | null>(null);
		const isHoveredRef = React.useRef(false);
		const isGloballyHoveredRef = React.useRef(false);

		// Use optimized click state only (doesn't cause re-renders during hover)
		const { isClicked, handleToggleClick } = useOptimizedClicked(coordinatesString);

		// Get store instance for direct subscription
		const store = useSearchPageInteractionStore();

		// Get property overlay hook from PropertyInteractionProvider
		const openPropertyOverlay = usePropertyInteractionStore(state => state.openPropertyOverlay);

		// Use granular selector to avoid re-renders when viewedProperties changes (Set reference)
		// but the count for this specific marker hasn't changed.
		// This prevents O(N) re-renders for all markers when one property is viewed.
		const viewCount = usePropertyInteractionStore(
			React.useCallback(
				state => {
					if (!marker.sups || !state.viewedProperties) return 0;
					let count = 0;
					for (const prop of marker.sups) {
						if (state.viewedProperties.has(prop._id)) {
							count++;
						}
					}
					return count;
				},
				[marker.sups],
			),
		);

		const count = React.useMemo(() => {
			return marker.sups ? marker.sups?.length || 0 : 0;
		}, [marker.sups]);

		const isViewed = viewCount > 0;
		const allViewed = viewCount === count;

		// Combined marker effects: secondary with scale on hover or clicked, else view icon with bg-accent no scale if viewed
		const applyMarkerEffects = React.useCallback(
			(isHovered: boolean) => {
				if (!safePinRef.current) return;

				const element = safePinRef.current;

				// Clear all classes first
				element.classList.remove(
					"scale-150",
					"!bg-secondary",
					"text-black",
					"shadow-lg",
					"!bg-accent",
					"text-accent-foreground",
				);

				// Apply effects based on priority: hover/clicked > viewed > default
				if (isHovered || isClicked) {
					// Secondary with scale on hover or clicked
					element.classList.add("scale-150", "!bg-secondary", "text-black", "shadow-lg");
					// } else if (isClicked) {
					// 	// Secondary with scale on hover or clicked
					// 	element.classList.add("!bg-accent", "text-black", "shadow-lg");
				}
				if (isViewed && !isHovered && !isClicked) {
					// View icon with bg-accent no scale if viewed
					element.classList.add("!bg-accent", "text-accent-foreground");
				}

				// Update AdvancedMarker zIndex
				if (markerRef.current) {
					const zIndex = isHovered || isGloballyHoveredRef.current || isClicked ? 9999 : 1;
					markerRef.current.zIndex = zIndex;
				}
			},
			[isClicked, isViewed],
		);

		// Apply effects when states change
		React.useEffect(() => {
			applyMarkerEffects(isHoveredRef.current || isGloballyHoveredRef.current);
		}, [applyMarkerEffects]);

		// Zero-rerender hover handlers
		const handleMouseEnter = React.useCallback(() => {
			isHoveredRef.current = true;
			applyMarkerEffects(true);
			// Update global state for cross-component sync
			store.getState().setHoveredMarkerId(coordinatesString);
		}, [applyMarkerEffects, coordinatesString, store]);

		const handleMouseLeave = React.useCallback(() => {
			isHoveredRef.current = false;
			applyMarkerEffects(false);
			// Clear global state
			store.getState().setHoveredMarkerId(null);
		}, [applyMarkerEffects, store]);

		// Listen for global hover changes without causing re-renders
		React.useEffect(() => {
			const unsubscribe = store.subscribe(
				state => state.hoveredMarkerId,
				hoveredId => {
					const wasGloballyHovered = isGloballyHoveredRef.current;
					isGloballyHoveredRef.current = hoveredId === coordinatesString;

					// Only update if global hover state changed
					if (wasGloballyHovered !== isGloballyHoveredRef.current) {
						applyMarkerEffects(isHoveredRef.current || isGloballyHoveredRef.current);
					}
				},
			);

			return unsubscribe;
		}, [coordinatesString, applyMarkerEffects, store]);

		const onClick = React.useCallback(() => {
			if (!marker || !coordinates) return;

			// Open property overlay with properties using new system
			if (marker.sups && marker.sups?.length > 0) {
				const supIds = marker.sups.map(sup => sup._id);
				const initialSupId = supIds[0];

				// Use the new PropertyOverlay system via PropertyInteractionProvider
				openPropertyOverlay({ sups: marker.sups, initialSupId, projectName: marker.projectName || "" });
			}

			// Toggle selection using optimized hook with proper marker data
			handleToggleClick({
				coordinates,
				projectId: marker.projectId,
				projectName: marker.projectName,
				sups: marker.sups,
			});

			// Don't pan to marker on click to avoid center shift
		}, [marker, coordinates, handleToggleClick, openPropertyOverlay]);

		if (!coordinates) return null;

		return (
			<ErrorBoundary fallback={MarkerFallback}>
				<AdvancedMarker
					key={`marker-${coordinatesString}`}
					position={{ lat: coordinates[1], lng: coordinates[0] }}
					onClick={onClick}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					zIndex={isClicked ? 9999 : 1}
					ref={markerRef}
				>
					<div className="relative">
						<SafePin ref={safePinRef} className={isViewed ? "!bg-accent" : ""}>
							{isViewed ? (
								<div className="flex items-center justify-center">
									{allViewed ? (
										<Eye className="w-3 h-3 text-white" />
									) : (
										<span className="text-[0.6rem] font-semibold text-white">
											{viewCount}/{count}
										</span>
									)}
								</div>
							) : (
								<div className="text-white font-semibold text-xs">{count}</div>
							)}
						</SafePin>
					</div>
				</AdvancedMarker>
			</ErrorBoundary>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison to prevent unnecessary re-renders
		const m1 = prevProps.marker;
		const m2 = nextProps.marker;

		// Deep compare coordinates (values instead of array reference)
		// marker._id is strictly number[] from store types
		const coordsEqual =
			m1._id === m2._id || // Reference check fallback
			(Array.isArray(m1._id) &&
				Array.isArray(m2._id) &&
				m1._id.length === 2 &&
				m2._id.length === 2 &&
				m1._id[0] === m2._id[0] &&
				m1._id[1] === m2._id[1]);

		if (!coordsEqual) return false;

		// Compare projectId
		if (m1.projectId !== m2.projectId) return false;

		// Deep compare sups IDs to ensure content hasn't changed
		return areSupsEqual(m1.sups, m2.sups);
	},
);
